import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { 
  BookOpen,
  Lightbulb,
  Zap,
  Sparkles,
  Bot
} from 'lucide-react';
import { ScribeManager } from '@/components/help/ScribeManager';
import { FeatureRequestsPreview } from '@/components/help/FeatureRequestsPreview';
import AssistantChat from '@/components/help/AssistantChat';

const Help: React.FC = () => {

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-400/10 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-60 h-60 bg-green-400/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            x: [-20, 20, -20],
            y: [-20, 20, -20],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-purple-100 px-4 py-2 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Help & Support Center</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-blue-800 to-purple-700 bg-clip-text text-transparent mb-4">
            Get the Help You Need
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore interactive guides, get instant AI assistance, and connect with our support team
          </p>
        </motion.div>

        {/* Full-Width Interactive Guides */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <div className="bg-gradient-to-r from-white via-blue-50/30 to-white p-8 rounded-2xl border border-blue-200/30 shadow-lg backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <motion.div 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-xl"
                whileHover={{ rotate: 5 }}
              >
                <BookOpen className="h-6 w-6 text-white" />
              </motion.div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Interactive Guides</h2>
                <p className="text-gray-600">Step-by-step tutorials to master Hivetivity features</p>
              </div>
            </div>
            <ScribeManager 
              className="space-y-6" 
              showSearch={true}
              defaultCategory="all"
            />
          </div>
        </motion.div>

        {/* AI Assistant and Feature Requests Side by Side */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* AI Assistant */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-blue-200/50 hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-xl">
                    <Bot className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">AI Assistant</CardTitle>
                    <CardDescription className="text-sm">Get instant, intelligent help</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white/70 backdrop-blur-sm p-6 rounded-xl border border-blue-200/30">
                  <div className="flex items-start gap-4">
                    <motion.div 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 p-2 rounded-lg"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Zap className="h-5 w-5 text-white" />
                    </motion.div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">AI-Powered Support</h3>
                      <p className="text-gray-600 mb-4 text-sm">
                        Ask questions in natural language and get instant, contextual answers about Hivetivity features.
                      </p>
                      <div className="max-h-96 overflow-y-auto">
                        <AssistantChat />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Feature Requests */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Card className="h-full bg-gradient-to-br from-purple-50 via-white to-pink-50 border-purple-200/50 hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-3 rounded-xl">
                    <Lightbulb className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Feature Requests</CardTitle>
                    <CardDescription className="text-sm">Popular community requests</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <FeatureRequestsPreview maxItems={5} showViewAll={true} />
              </CardContent>
            </Card>
          </motion.div>
        </div>

      </div>
    </div>
  );
};

export default Help;